import { forumPostsPath } from '@communio/forum-query';
import { apiFetch } from '@/platform/lib/api';

export type ForumSort = 'hot' | 'newest' | 'oldest' | 'most_replies';
export type Stance = 'agree' | 'disagree';

export interface ForumPost {
  id: string;
  authorId: string;
  title: string;
  body: string;
  subSlug?: string;
  artifactType?: string;
  artifactId?: string;
  hotScore: number;
  agreeCount: number;
  disagreeCount: number;
  replyCount: number;
  createdAt: string;
  myStance?: Stance | null;
  watching?: boolean;
  imageObjectPaths?: string[];
  imageUrls?: string[];
  youtubeVideoId?: string;
}

export interface ForumComment {
  id: string;
  authorId: string;
  body: string;
  parentId?: string;
  createdAt: string;
  agreeCount?: number;
  disagreeCount?: number;
  myStance?: Stance | null;
}

export function listForumPosts(params?: { sort?: ForumSort; subSlug?: string; q?: string }) {
  return apiFetch<ForumPost[]>(forumPostsPath(params));
}

export function getForumPost(id: string) {
  return apiFetch<ForumPost>(`/forum/posts/${id}`);
}

export function getForumPostByArtifact(artifactType: string, artifactId: string) {
  const q = new URLSearchParams({ artifactType, artifactId });
  return apiFetch<ForumPost>(`/forum/posts/by-artifact?${q}`);
}

export function listForumComments(postId: string) {
  return apiFetch<ForumComment[]>(`/forum/posts/${postId}/comments`);
}

export function createForumComment(postId: string, body: string, parentId?: string) {
  return apiFetch<{ id: string }>(`/forum/posts/${postId}/comments`, {
    method: 'POST',
    body: JSON.stringify({ body, parentId: parentId ?? '' }),
  });
}

export function createForumPost(
  title: string,
  body: string,
  subSlug: string,
  media?: { imageObjectPaths?: string[]; youtubeUrl?: string },
) {
  return apiFetch<{ id: string }>('/forum/posts', {
    method: 'POST',
    body: JSON.stringify({
      title,
      body,
      subSlug,
      imageObjectPaths: media?.imageObjectPaths ?? [],
      youtubeUrl: media?.youtubeUrl ?? '',
    }),
  });
}

export function getPostWatch(postId: string) {
  return apiFetch<{ watching: boolean }>(`/forum/posts/${postId}/watch`);
}

export function setPostWatch(postId: string, watching: boolean) {
  return apiFetch<{ watching: boolean }>(`/forum/posts/${postId}/watch`, {
    method: 'PUT',
    body: JSON.stringify({ watching }),
  });
}

export function setPostReaction(postId: string, stance: Stance | null) {
  return apiFetch<{ agreeCount: number; disagreeCount: number; myStance?: Stance }>(
    `/forum/posts/${postId}/reaction`,
    { method: 'POST', body: JSON.stringify({ stance: stance ?? '' }) },
  );
}

export function setCommentReaction(postId: string, commentId: string, stance: Stance | null) {
  return apiFetch<{ agreeCount: number; disagreeCount: number; myStance?: Stance }>(
    `/forum/posts/${postId}/comments/${commentId}/reaction`,
    { method: 'POST', body: JSON.stringify({ stance: stance ?? '' }) },
  );
}

export function getForumPostEmoji(postId: string) {
  return apiFetch<{ emoji: string }>(`/forum/posts/${postId}/emoji`);
}

export function getForumCommentEmoji(postId: string, commentId: string) {
  return apiFetch<{ emoji: string }>(`/forum/posts/${postId}/comments/${commentId}/emoji`);
}

export function setPostEmoji(postId: string, emoji: string) {
  return apiFetch<{ emoji: string }>(`/forum/posts/${postId}/emoji`, {
    method: 'POST',
    body: JSON.stringify({ emoji }),
  });
}

export function setCommentEmoji(postId: string, commentId: string, emoji: string) {
  return apiFetch<{ emoji: string }>(`/forum/posts/${postId}/comments/${commentId}/emoji`, {
    method: 'POST',
    body: JSON.stringify({ emoji }),
  });
}

export function patchForumPost(postId: string, body: { title?: string; body?: string }) {
  return apiFetch<ForumPost>(`/forum/posts/${postId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function patchForumComment(postId: string, commentId: string, body: string) {
  return apiFetch<ForumComment>(`/forum/posts/${postId}/comments/${commentId}`, {
    method: 'PATCH',
    body: JSON.stringify({ body }),
  });
}

export function deleteForumPost(postId: string) {
  return apiFetch<void>(`/forum/posts/${postId}`, { method: 'DELETE' });
}

export function deleteForumComment(postId: string, commentId: string) {
  return apiFetch<void>(`/forum/posts/${postId}/comments/${commentId}`, { method: 'DELETE' });
}
